import React, { useMemo } from "react";
import { Chessboard } from "react-chessboard";

// Cburnett SVG pieces (Lichess) served locally from /public/pieces.
// The `pieces` object is defined at module scope so its reference is stable
// across renders — critical for react-chessboard's internal shallow-compare
// which decides whether to re-render squares.
const PIECE_CODES = ["wK", "wQ", "wR", "wB", "wN", "wP", "bK", "bQ", "bR", "bB", "bN", "bP"];

const PieceImg = React.memo(function PieceImg({ code }) {
  return (
    <img
      src={`/pieces/${code}.svg`}
      alt={code}
      draggable={false}
      style={{ width: "100%", height: "100%", pointerEvents: "none", userSelect: "none", display: "block" }}
    />
  );
});

const cburnettPieces = PIECE_CODES.reduce((acc, code) => {
  acc[code] = () => <PieceImg code={code} />;
  return acc;
}, {});

const DARK_SQUARE = { backgroundColor: "#4A3F35" };
const LIGHT_SQUARE = { backgroundColor: "#EADDCA" };
const BOARD_STYLE = { borderRadius: 4, border: "1px solid rgba(239,232,216,0.15)" };

function BoardImpl({
  position,
  onPieceDrop,
  onSquareClick,
  boardOrientation = "white",
  arePiecesDraggable = true,
  customSquareStyles,
  animationDuration = 250,
  showCoordinates = true,
}) {
  // Adapt v4-style onPieceDrop(from, to, piece) → v5 signature.
  const handleDrop = useMemo(() => {
    if (!onPieceDrop) return undefined;
    return ({ piece, sourceSquare, targetSquare }) => {
      if (!targetSquare) return false;
      const pieceStr = piece?.pieceType || "";
      return !!onPieceDrop(sourceSquare, targetSquare, pieceStr);
    };
  }, [onPieceDrop]);

  // Memoize options so the same identity is passed to react-chessboard
  // whenever the underlying values are unchanged.
  const options = useMemo(
    () => ({
      id: "board",
      position,
      boardOrientation,
      pieces: cburnettPieces,
      darkSquareStyle: DARK_SQUARE,
      lightSquareStyle: LIGHT_SQUARE,
      boardStyle: BOARD_STYLE,
      squareStyles: customSquareStyles || {},
      allowDragging: arePiecesDraggable,
      animationDurationInMs: animationDuration,
      showAnimations: true,
      showNotation: showCoordinates,
      onPieceDrop: handleDrop,
      onSquareClick,
    }),
    [position, boardOrientation, customSquareStyles, arePiecesDraggable, animationDuration, showCoordinates, handleDrop, onSquareClick],
  );

  return (
    <div className="board-wrap w-full" data-testid="chessboard">
      <Chessboard options={options} />
    </div>
  );
}

// Board itself is memoized so that unless one of its props changes,
// no work is done on the chessboard subtree.
const Board = React.memo(BoardImpl);
export default Board;
